import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Option } from '@sapphire/result';
import {
  filter,
  map,
  MonoTypeOperatorFunction,
  Observable,
  of,
  switchMap,
  take,
} from 'rxjs';

import { AuthSelectorsFacade } from '@ps/auth-data-access';
import { Survey } from '@ps/survey-utils';
import {
  selectAllBuyerSettings,
  UserSettingsSelectorsFacade,
} from '@ps/user-settings-data-access';

import {
  selectSurvey,
  selectSurveyWorkflowPage,
} from '../../core/survey-creation-core.selectors';
import {
  Insight,
  isInsightAllowed,
  readsBuyerSetting,
} from './insight-rules.config';

/**
 * Lets an insight's API call through or stops it, by checking its rule against
 * the open page, the survey, the current viewer and the buyer's settings.
 */
@Injectable({ providedIn: 'root' })
export class InsightGate {
  private readonly _store = inject(Store);
  private readonly _authSelectors = inject(AuthSelectorsFacade);
  private readonly _userSettingsSelectors = inject(UserSettingsSelectorsFacade);

  private readonly _openPage = this._store.selectSignal(
    selectSurveyWorkflowPage,
  );
  private readonly _activeSurvey = this._store.selectSignal(selectSurvey);

  /**
   * Immediate yes/no for a given survey: a child row, or a survey an action
   * just loaded. Only for rules that read no buyer setting — `onlyIfAllowed`
   * waits for those.
   */
  public isAllowedForSurvey(insight: Insight, survey: Option<Survey>): boolean {
    return isInsightAllowed(insight, {
      page: this._openPage(),
      survey,
      hasImpersonatedBuyer: this._authSelectors.hasImpersonatedBuyer(),
      buyerSettings: this._userSettingsSelectors.buyerSettings(),
    });
  }

  /**
   * RxJS filter: lets each value through only if `insight` is allowed for the
   * survey `pickSurvey` takes out of that value (the active survey unless told
   * otherwise; e.g. the survey an action just loaded, or none on the create
   * page). A rule behind a buyer setting waits for the settings to load first,
   * so a value that beats them is decided late instead of dropped; while
   * waiting, a newer value replaces an older one (each recomputes from current
   * state, so only the latest matters).
   */
  public onlyIfAllowed<T>(
    insight: Insight,
    pickSurvey: (value: T) => Option<Survey> = () =>
      Option.from(this._activeSurvey()),
  ): MonoTypeOperatorFunction<T> {
    return switchMap((value: T) =>
      this._readyToDecide$(insight).pipe(
        filter(() => this.isAllowedForSurvey(insight, pickSurvey(value))),
        map(() => value),
      ),
    );
  }

  // Emits once the rule can be decided: right away, or after the buyer
  // settings load when the rule reads a buyer setting.
  private _readyToDecide$(insight: Insight): Observable<unknown> {
    return readsBuyerSetting(insight)
      ? this._store.select(selectAllBuyerSettings).pipe(
          filter((settings) => !!settings.id),
          take(1),
        )
      : of(true);
  }
}
